// 學生上傳作文時的前端錯誤紀錄（kanban 1003072）。
//
// 上傳頁在前端擋下、並顯示錯誤給學生的每一處（檔案被略過、PDF 展開失敗、圖片無法讀取、
// 張數超標、旋轉視窗載入失敗、送出失敗），另外送一筆紀錄到後端 POST /clienterrorlog，
// 事後才查得到「哪個學生、哪個檔、卡在哪一層」。
//
// 兩條底線：
// - 學生看到的畫面與行為一律不變：紀錄只在既有的錯誤處理旁邊「多送一筆」，
//   不改訊息、不改流程。
// - 送不出去就算了：這裡的每一支都不 throw，回傳的 Promise 也永遠 resolve；
//   呼叫端 fire-and-forget（`void report...`），不 await 在學生操作的關鍵路徑上。
//
// 欄位上限照前後端共用的規格（後端超過也只截斷不拒收，但整包 > 8192 bytes 會回 413，
// 所以前端先截好、量好再送）。不送作文內容（文字模式只記字數）、不送姓名與 IP。

import { inspectImageStructure, type ImageStructureSummary } from "./imageUtils";

export const CLIENT_ERROR_KINDS = [
  "file_rejected",
  "pdf_expand_failed",
  "image_unreadable",
  "too_many_pages",
  "rotate_image_load_failed",
  "submit_failed_before_navigate",
  "submit_failed_background",
] as const;

export type ClientErrorKind = (typeof CLIENT_ERROR_KINDS)[number];

/** detail 的值只能是扁平的 str／int／float／bool／null（巢狀物件或陣列後端回 422）。 */
export type ClientErrorDetailValue = string | number | boolean | null;
export type ClientErrorDetail = Record<string, ClientErrorDetailValue>;
/** 組 detail 時允許 undefined（取不到的值），整理時丟掉。 */
export type ClientErrorDetailInput = Record<string, ClientErrorDetailValue | undefined>;

/** 身分、題目與頁面：由上傳頁在「事情發生當下」取好傳進來。 */
export interface ClientErrorLogContext {
  studentID: string;
  classCode: string;
  className: string;
  bookID: string;
  chapterID: string;
  chapter: string;
  uploadMode: string;
  page: string;
}

export interface ClientEnvironment {
  userAgent: string;
  bundle: string;
}

export interface ClientErrorLogPayload extends ClientErrorLogContext, ClientEnvironment {
  kind: ClientErrorKind;
  message: string;
  detail: ClientErrorDetail;
}

export const EMPTY_CLIENT_ERROR_CONTEXT: ClientErrorLogContext = {
  studentID: "",
  classCode: "",
  className: "",
  bookID: "",
  chapterID: "",
  chapter: "",
  uploadMode: "",
  page: "",
};

/** 各欄位的字元上限（規格表）。 */
export const CLIENT_ERROR_FIELD_LIMITS = {
  message: 500,
  studentID: 100,
  classCode: 50,
  className: 100,
  bookID: 100,
  chapterID: 100,
  chapter: 200,
  uploadMode: 20,
  page: 200,
  userAgent: 500,
  bundle: 100,
} as const;

export const DETAIL_MAX_KEYS = 40;
export const DETAIL_KEY_MAX_LENGTH = 50;
export const DETAIL_STRING_MAX_LENGTH = 300;
/** 整個 request body 的上限；超過後端回 413。 */
export const PAYLOAD_MAX_BYTES = 8192;
/** 同一次選圖最多送幾筆（被略過的檔、壞圖、張數超標等合計）。 */
export const MAX_ENTRIES_PER_BATCH = 5;
/** 檔頭／檔尾各取幾 bytes 轉 hex（壞圖、PDF 展開失敗、旋轉視窗載入失敗）。 */
export const EDGE_BYTES = 64;
/**
 * 被略過的檔（file_rejected）只送檔頭 16 bytes：夠看出真正的格式（HEIC、ZIP、PDF…），
 * 又不會帶出太多檔案內容。text/* 的檔完全不讀（被擋的文字檔可能就是作文本身）。
 */
export const REJECTED_FILE_HEAD_BYTES = 16;
/** 錯誤訊息（Error.message）的上限，與 detail 字串上限相同。 */
export const ERROR_MESSAGE_MAX_LENGTH = 300;

type ContextField = Exclude<keyof typeof CLIENT_ERROR_FIELD_LIMITS, "message" | "userAgent" | "bundle">;
const CONTEXT_FIELDS: ContextField[] = [
  "studentID",
  "classCode",
  "className",
  "bookID",
  "chapterID",
  "chapter",
  "uploadMode",
  "page",
];

/**
 * 孤立的代理字元（前後沒有配對的 D800–DFFF）換成 U+FFFD，長度不變。
 * 與 String.prototype.toWellFormed 相同；那支是 ES2024，舊瀏覽器沒有時用這支。
 * 刻意不用 regex 的 lookbehind —— 舊版 Safari 不支援，整個模組會載入失敗。
 */
export const replaceLoneSurrogates = (text: string): string => {
  let out = "";
  let changed = false;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        out += text[i] + text[i + 1];
        i++;
      } else {
        out += "\ufffd";
        changed = true;
      }
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      out += "\ufffd";
      changed = true;
    } else {
      out += text[i];
    }
  }
  return changed ? out : text;
};

const nativeToWellFormed = (String.prototype as { toWellFormed?: (this: string) => string })
  .toWellFormed;

/** 轉成合法 UTF-16：後端遇到孤立代理字元會整筆回 422。 */
const toWellFormedString = (text: string): string =>
  typeof nativeToWellFormed === "function" ? nativeToWellFormed.call(text) : replaceLoneSurrogates(text);

/**
 * 轉成字串、截到 max 個 UTF-16 單位（null／undefined → 空字串），再轉成合法 UTF-16。
 * 不在代理對中間切斷；UTF-16 單位數 ≥ 字元數，所以後端以字元計的上限一定守得住。
 * 送出的每個字串欄位與 detail 的 key、字串值都經過這裡。
 */
export const truncateText = (value: unknown, max: number): string => {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : String(value);
  if (text.length <= max) return toWellFormedString(text);
  let cut = text.slice(0, Math.max(0, max));
  const last = cut.charCodeAt(cut.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) cut = cut.slice(0, -1);
  return toWellFormedString(cut);
};

/**
 * 整理成後端收得下的扁平 detail：丟掉 undefined、巢狀物件、陣列、函式、BigInt
 * （BigInt 會讓 JSON.stringify 直接 throw）；非有限數字轉 null；字串截 300、key 截 50、
 * 最多 40 個 key。
 */
export const sanitizeDetail = (detail?: ClientErrorDetailInput | null): ClientErrorDetail => {
  if (!detail || typeof detail !== "object") return {};
  const entries: Array<[string, ClientErrorDetailValue]> = [];
  const seen = new Set<string>();
  for (const [rawKey, value] of Object.entries(detail)) {
    if (entries.length >= DETAIL_MAX_KEYS) break;
    let clean: ClientErrorDetailValue;
    if (value === null || typeof value === "boolean") clean = value;
    else if (typeof value === "number") clean = Number.isFinite(value) ? value : null;
    else if (typeof value === "string") clean = truncateText(value, DETAIL_STRING_MAX_LENGTH);
    else continue;
    const key = truncateText(rawKey, DETAIL_KEY_MAX_LENGTH);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    entries.push([key, clean]);
  }
  // fromEntries 建的是自有屬性，連 "__proto__" 這種 key 也不會改到原型
  return Object.fromEntries(entries);
};

const textEncoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;

/** JSON 序列化後的 UTF-8 bytes 數（沒有 TextEncoder 時用每個 UTF-16 單位 3 bytes 估上限）。 */
export const payloadByteLength = (payload: unknown): number => {
  const json = JSON.stringify(payload) ?? "";
  return textEncoder ? textEncoder.encode(json).length : json.length * 3;
};

const halve = (text: string): string => truncateText(text, Math.floor(text.length / 2));

/** 縮短字串時，detail 的長字串與 message 先縮到這個長度為止，再往下一步。 */
const SHRINK_FLOOR = 32;

/**
 * 確保 JSON 後 ≤ maxBytes。依序：
 * 1. detail 裡最長的字串砍半，直到每個都 ≤ 32 字元
 * 2. message 砍半，直到 ≤ 32 字元
 * 3. 從最後一個 key 開始丟 detail
 * 4. 其餘字串欄位（含 message）最長的砍半，直到放得下或都是空字串
 * 正常的紀錄遠小於上限，這段只防異常長的檔名、題目名稱或後端回的 detail。
 */
export const fitPayloadToByteLimit = (
  payload: ClientErrorLogPayload,
  maxBytes: number = PAYLOAD_MAX_BYTES
): ClientErrorLogPayload => {
  const current: ClientErrorLogPayload = { ...payload, detail: { ...payload.detail } };
  const fits = () => payloadByteLength(current) <= maxBytes;
  if (fits()) return current;

  for (;;) {
    let longestKey: string | null = null;
    let longestLength = SHRINK_FLOOR;
    for (const [key, value] of Object.entries(current.detail)) {
      if (typeof value === "string" && value.length > longestLength) {
        longestKey = key;
        longestLength = value.length;
      }
    }
    if (longestKey === null) break;
    current.detail[longestKey] = halve(current.detail[longestKey] as string);
    if (fits()) return current;
  }

  while (current.message.length > SHRINK_FLOOR) {
    current.message = halve(current.message);
    if (fits()) return current;
  }

  const keys = Object.keys(current.detail);
  while (keys.length > 0) {
    delete current.detail[keys.pop() as string];
    if (fits()) return current;
  }

  const stringFields = ["message", ...CONTEXT_FIELDS, "userAgent", "bundle"] as const;
  for (;;) {
    let longest: (typeof stringFields)[number] | null = null;
    for (const field of stringFields) {
      if (current[field].length > 0 && (longest === null || current[field].length > current[longest].length)) {
        longest = field;
      }
    }
    if (longest === null) return current;
    current[longest] = halve(current[longest]);
    if (fits()) return current;
  }
};

/** 組出要送的 payload：各欄位截到上限、detail 整理成扁平，最後量整包大小。 */
export const buildClientErrorLogPayload = (input: {
  kind: ClientErrorKind;
  message?: unknown;
  context?: Partial<ClientErrorLogContext> | null;
  detail?: ClientErrorDetailInput | null;
  environment?: Partial<ClientEnvironment> | null;
}): ClientErrorLogPayload => {
  const context = input.context ?? {};
  const environment = input.environment ?? {};
  const payload: ClientErrorLogPayload = {
    kind: input.kind,
    message: truncateText(input.message, CLIENT_ERROR_FIELD_LIMITS.message),
    ...EMPTY_CLIENT_ERROR_CONTEXT,
    userAgent: truncateText(environment.userAgent, CLIENT_ERROR_FIELD_LIMITS.userAgent),
    bundle: truncateText(environment.bundle, CLIENT_ERROR_FIELD_LIMITS.bundle),
    detail: sanitizeDetail(input.detail),
  };
  for (const field of CONTEXT_FIELDS) {
    payload[field] = truncateText(context[field], CLIENT_ERROR_FIELD_LIMITS[field]);
  }
  return fitPayloadToByteLimit(payload);
};

/**
 * 入口 bundle 檔名（例 index-g05vyKYZ.js），用來分辨學生當時跑的是哪一版。
 * 專案沒有 build 版本號，vite 產出的入口檔名帶內容雜湊，一版一個。找不到（dev server）為空字串。
 */
export const findEntryBundleName = (scriptSrcs: Iterable<string | null | undefined>): string => {
  for (const src of scriptSrcs) {
    const match = /\/assets\/(index-[^/?#]+\.js)(?:[?#]|$)/.exec(src ?? "");
    if (match) return match[1];
  }
  return "";
};

/** 送出當下的瀏覽器資訊；拿不到就是空字串。 */
export const readClientEnvironment = (): ClientEnvironment => {
  let userAgent = "";
  let bundle = "";
  try {
    if (typeof navigator !== "undefined") userAgent = navigator.userAgent || "";
  } catch {
    // 拿不到就空著
  }
  try {
    if (typeof document !== "undefined" && document.scripts) {
      bundle = findEntryBundleName(Array.from(document.scripts, (script) => script.src));
    }
  } catch {
    // 拿不到就空著
  }
  return { userAgent, bundle };
};

/**
 * 目前頁面的 pathname。送出失敗的背景紀錄執行時網址已是 /student-home，
 * 所以呼叫端要在導頁「之前」取好。
 */
export const readCurrentPage = (): string => {
  try {
    return typeof window !== "undefined" ? window.location.pathname : "";
  } catch {
    return "";
  }
};

const hexOf = (value: number): string => value.toString(16).padStart(2, "0");

export const bytesToHex = (bytes: Uint8Array): string => Array.from(bytes, hexOf).join("");

/** 前 n bytes 的 hex（預設 64 bytes = 128 個 hex 字元）。 */
export const headHex = (bytes: Uint8Array, n: number = EDGE_BYTES): string =>
  bytesToHex(bytes.subarray(0, Math.max(0, n)));

/** 後 n bytes 的 hex；檔案比 n 短就是整個檔。 */
export const tailHex = (bytes: Uint8Array, n: number = EDGE_BYTES): string =>
  bytesToHex(bytes.subarray(Math.max(0, bytes.length - Math.max(0, n))));

/** 檔名、類型、大小、修改時間；沒有檔案時全是 undefined（整理時丟掉）。 */
export const describeFileForLog = (file?: File | null): ClientErrorDetailInput => ({
  fileName: file?.name,
  fileType: file?.type,
  fileSize: file?.size,
  fileLastModified: file?.lastModified,
});

/**
 * 只讀檔頭（與檔尾）的切片（預設各 64 bytes）：被擋的檔可能是 400MB 的 PDF，
 * 不能整份讀進來。讀不到就不帶。
 */
export const readFileEdgesForLog = async (
  file?: Blob | null,
  options: { tail?: boolean; headBytes?: number } = {}
): Promise<ClientErrorDetailInput> => {
  const out: ClientErrorDetailInput = {};
  if (!file || typeof file.slice !== "function") return out;
  const read = async (part: Blob): Promise<Uint8Array> => new Uint8Array(await part.arrayBuffer());
  try {
    out.headHex = bytesToHex(await read(file.slice(0, options.headBytes ?? EDGE_BYTES)));
  } catch {
    // 讀不到就不帶
  }
  if (options.tail) {
    try {
      out.tailHex = bytesToHex(await read(file.slice(Math.max(0, file.size - EDGE_BYTES))));
    } catch {
      // 讀不到就不帶
    }
  }
  return out;
};

export interface FileInspection {
  summary: ImageStructureSummary;
  headHex: string;
  tailHex: string;
}

/**
 * 整份讀進來走一次結構檢查（inspectImageStructure）。只對已判壞的圖做 ——
 * 那些是通過 10MB 門檻的圖或 PDF 轉出來的頁，大小有上限。讀檔失敗回 null。
 */
export const inspectFileBytes = async (file?: Blob | null): Promise<FileInspection | null> => {
  try {
    if (!file || typeof file.arrayBuffer !== "function") return null;
    const bytes = new Uint8Array(await file.arrayBuffer());
    return { summary: inspectImageStructure(bytes), headHex: headHex(bytes), tailHex: tailHex(bytes) };
  } catch {
    return null;
  }
};

/** 結構摘要攤成 detail 的 key；沒讀到檔就空物件。 */
export const structureDetail = (inspection: FileInspection | null): ClientErrorDetailInput => {
  if (!inspection) return {};
  const { summary } = inspection;
  return {
    structureFormat: summary.format,
    structureComplete: summary.complete,
    endOffset: summary.endOffset,
    trailingBytes: summary.trailingBytes,
    stopReason: summary.stopReason,
    lastMarker: summary.lastMarker,
    lastMarkerOffset: summary.lastMarkerOffset,
    headHex: inspection.headHex,
    tailHex: inspection.tailHex,
  };
};

/**
 * 「圖片無法讀取」是哪一層判壞的（排除法）：isFileDecodable 先驗結構、再驗 decode。
 * 結構不完整 → structure；結構完整卻被判壞 → decode；讀不到檔無從判斷 → unknown。
 */
export const failedLayerOf = (inspection: FileInspection | null): "structure" | "decode" | "unknown" => {
  if (!inspection) return "unknown";
  return inspection.summary.complete ? "decode" : "structure";
};

/**
 * FastAPI 422 的 detail 是陣列，每一項除了 type／loc／msg 還帶 input（學生送出的值，
 * 可能是整段作文）、ctx、url。紀錄只留前三個；不是物件的項目原樣保留。
 */
const pickValidationFields = (item: unknown): unknown => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;
  const picked: Record<string, unknown> = {};
  for (const key of ["type", "loc", "msg"]) {
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      picked[key] = (item as Record<string, unknown>)[key];
    }
  }
  return picked;
};

/**
 * 送出失敗的技術細節。extractSubmitErrorMessage 只給學生看的文案，這裡另取：
 * - httpStatus（response.status）
 * - errorCode（axios 的 code，如 ERR_NETWORK、ECONNABORTED；DOMException 的數字 code
 *   也收，但 0 與空字串沒有資訊量，不送）
 * - errorName、errorMessage（Error.name／message，DOMException 也是；只在 error 是 Error
 *   或 message 是字串時取，不用 String(error) —— 那可能把任意物件的內容序列化出去）
 * - responseDetail（後端的 detail；字串原樣、陣列每項只留 type／loc／msg，
 *   其他形狀 JSON.stringify，序列化不了就不帶，長度由 detail 上限截斷）
 * - requestPath（config.url，去掉 query 與 hash）
 * 拿不到的不帶。
 */
export const extractSubmitErrorDetail = (error: unknown): ClientErrorDetailInput => {
  const out: ClientErrorDetailInput = {};
  if (!error || typeof error !== "object") return out;
  try {
    const err = error as {
      response?: { status?: unknown; data?: unknown };
      code?: unknown;
      config?: { url?: unknown };
      name?: unknown;
      message?: unknown;
    };
    const status = err.response?.status;
    if (typeof status === "number") out.httpStatus = status;
    const code = err.code;
    if ((typeof code === "string" && code !== "") || (typeof code === "number" && code !== 0)) {
      out.errorCode = code;
    }
    if (error instanceof Error || typeof err.message === "string") {
      if (typeof err.name === "string" && err.name) out.errorName = err.name;
      if (typeof err.message === "string") {
        out.errorMessage = truncateText(err.message, ERROR_MESSAGE_MAX_LENGTH);
      }
    }
    const data = err.response?.data;
    const detail =
      data && typeof data === "object" ? (data as { detail?: unknown }).detail : undefined;
    if (typeof detail === "string") {
      out.responseDetail = detail;
    } else if (detail !== undefined && detail !== null) {
      try {
        const shown = Array.isArray(detail) ? detail.map(pickValidationFields) : detail;
        const json = JSON.stringify(shown);
        if (typeof json === "string") out.responseDetail = json;
      } catch {
        // 序列化不了就不帶；不退回 String(detail)，那會繞過上面的欄位篩選
      }
    }
    const url = err.config?.url;
    if (typeof url === "string") out.requestPath = url.split(/[?#]/)[0];
  } catch {
    // 取值過程出錯就送已經取到的
  }
  return out;
};

/** 圖片模式送出的張數與總 bytes（空格略過）。 */
export const imageSubmitStats = (
  files: ReadonlyArray<Blob | null | undefined> | null | undefined
): ClientErrorDetailInput => {
  const present = (files ?? []).filter((file): file is Blob => !!file);
  return {
    imageCount: present.length,
    totalBytes: present.reduce((sum, file) => sum + (typeof file.size === "number" ? file.size : 0), 0),
  };
};

/** 一筆待送的紀錄。enrich 是送之前才做的讀檔分析，失敗就只送 detail。 */
export interface PendingClientError {
  kind: ClientErrorKind;
  message: string;
  detail?: ClientErrorDetailInput;
  enrich?: () => Promise<ClientErrorDetailInput>;
}

const isTextFile = (file?: File | null): boolean => /^text\//i.test(file?.type ?? "");

export const rejectedFileError = (
  message: string,
  rejectCode: string,
  file?: File
): PendingClientError => ({
  kind: "file_rejected",
  message,
  detail: { rejectCode, ...describeFileForLog(file) },
  // 檔頭只取 16 bytes；text/* 完全不讀（見 REJECTED_FILE_HEAD_BYTES）
  enrich: async () =>
    isTextFile(file) ? {} : readFileEdgesForLog(file, { headBytes: REJECTED_FILE_HEAD_BYTES }),
});

export const pdfExpandFailedError = (message: string, file?: File): PendingClientError => ({
  kind: "pdf_expand_failed",
  message,
  detail: describeFileForLog(file),
  enrich: () => readFileEdgesForLog(file, { tail: true }),
});

export const unreadableImageError = (
  message: string,
  file: File,
  fromPdf: boolean | undefined
): PendingClientError => ({
  kind: "image_unreadable",
  message,
  detail: { ...describeFileForLog(file), fromPdf },
  enrich: async () => {
    const inspection = await inspectFileBytes(file);
    return { ...structureDetail(inspection), failedLayer: failedLayerOf(inspection) };
  },
});

export const tooManyPagesError = (message: string, detail: ClientErrorDetailInput): PendingClientError => ({
  kind: "too_many_pages",
  message,
  detail,
});

export const rotateImageLoadFailedError = (message: string, file?: File): PendingClientError => ({
  kind: "rotate_image_load_failed",
  message,
  detail: describeFileForLog(file),
  enrich: async () => structureDetail(await inspectFileBytes(file)),
});

export const submitFailedError = (
  kind: "submit_failed_before_navigate" | "submit_failed_background",
  message: string,
  error: unknown,
  extra: ClientErrorDetailInput
): PendingClientError => ({
  kind,
  message,
  detail: { ...extractSubmitErrorDetail(error), ...extra },
});

/**
 * 一次選圖超過 max 筆時挑哪幾筆送：先保證每種 kind 至少一筆（依出現順序），
 * 剩下的名額照原順序補。回傳的是原陣列的 index，由小到大。
 */
export const selectBatchIndices = (
  entries: ReadonlyArray<{ kind: string }>,
  max: number = MAX_ENTRIES_PER_BATCH
): number[] => {
  const picked = new Set<number>();
  const kinds = new Set<string>();
  entries.forEach((entry, index) => {
    if (picked.size < max && !kinds.has(entry.kind)) {
      kinds.add(entry.kind);
      picked.add(index);
    }
  });
  for (let index = 0; index < entries.length && picked.size < max; index++) picked.add(index);
  return [...picked].sort((a, b) => a - b);
};

export type ClientErrorSender = (payload: ClientErrorLogPayload) => unknown;

/**
 * 送出一批紀錄；永遠 resolve、不 throw。
 * batch = true（同一次選圖）：最多送 5 筆，每筆 detail 帶 batchIndex（在這次全部紀錄中的
 * 第幾筆，從 1 起算）與 batchTotal（這次全部幾筆，含沒送的）。
 * 讀檔分析（enrich）逐筆做，避免好幾份大檔同時讀進記憶體。
 */
export const reportClientErrors = async (
  send: ClientErrorSender,
  context: Partial<ClientErrorLogContext> | null,
  entries: ReadonlyArray<PendingClientError>,
  options: { batch?: boolean; environment?: ClientEnvironment } = {}
): Promise<void> => {
  try {
    if (!entries || entries.length === 0) return;
    const indices = options.batch ? selectBatchIndices(entries) : entries.map((_, index) => index);
    const environment = options.environment ?? readClientEnvironment();
    const sending: Promise<unknown>[] = [];
    for (const index of indices) {
      const entry = entries[index];
      let enriched: ClientErrorDetailInput = {};
      if (entry.enrich) {
        try {
          enriched = (await entry.enrich()) ?? {};
        } catch {
          // 讀檔分析失敗就只送已有的 detail
        }
      }
      const detail: ClientErrorDetailInput = {
        ...(options.batch ? { batchIndex: index + 1, batchTotal: entries.length } : {}),
        ...entry.detail,
        ...enriched,
      };
      const payload = buildClientErrorLogPayload({
        kind: entry.kind,
        message: entry.message,
        context,
        detail,
        environment,
      });
      sending.push(
        Promise.resolve()
          .then(() => send(payload))
          .catch(() => undefined)
      );
    }
    await Promise.all(sending);
  } catch {
    // 紀錄送不出去不影響畫面
  }
};

/** 單筆（旋轉視窗、送出失敗）。 */
export const reportClientError = (
  send: ClientErrorSender,
  context: Partial<ClientErrorLogContext> | null,
  entry: PendingClientError,
  options: { environment?: ClientEnvironment } = {}
): Promise<void> => reportClientErrors(send, context, [entry], options);

/** 同一次選圖的一批（最多 5 筆，帶 batchIndex／batchTotal）。 */
export const reportClientErrorBatch = (
  send: ClientErrorSender,
  context: Partial<ClientErrorLogContext> | null,
  entries: ReadonlyArray<PendingClientError>,
  options: { environment?: ClientEnvironment } = {}
): Promise<void> => reportClientErrors(send, context, entries, { ...options, batch: true });
